import { DEFAULT_DESIGN, HISTORY_LIMIT } from '../config/defaults';
import type {
  ErrorCorrectionLevel,
  InnerEyeStyle,
  ModuleStyle,
  OuterEyeStyle,
  QrDesign,
} from '../domain/types';
import { createHistory, record, redo, undo, type History } from './history';

export type QrAction =
  | { readonly type: 'setContent'; readonly content: string }
  | { readonly type: 'setErrorCorrectionLevel'; readonly level: ErrorCorrectionLevel }
  | { readonly type: 'setSize'; readonly size: number }
  | { readonly type: 'setModuleStyle'; readonly style: ModuleStyle }
  | { readonly type: 'setModuleColor'; readonly color: string }
  | { readonly type: 'setBackgroundColor'; readonly color: string }
  | { readonly type: 'setOuterEyeStyle'; readonly style: OuterEyeStyle }
  | { readonly type: 'setInnerEyeStyle'; readonly style: InnerEyeStyle }
  | { readonly type: 'setEyeColor'; readonly color: string }
  | { readonly type: 'setOuterBorderRadius'; readonly radius: number }
  | { readonly type: 'setInnerBorderRadius'; readonly radius: number }
  | { readonly type: 'setStyleEyesAsModules'; readonly enabled: boolean }
  | { readonly type: 'setCenterImage'; readonly file: File | null }
  | { readonly type: 'setCenterImageSize'; readonly size: number }
  | { readonly type: 'setCustomModuleImage'; readonly file: File | null }
  | { readonly type: 'reset' }
  | { readonly type: 'undo' }
  | { readonly type: 'redo' };

export type QrState = History<QrDesign>;

export function createInitialState(design: QrDesign = DEFAULT_DESIGN): QrState {
  return createHistory(design);
}

/**
 * Maps an action to the next design plus a coalescing tag.
 *
 * A non-null tag means "consecutive edits of this kind are one undo step" —
 * used for continuous inputs (typing, slider drags, colour picking) so the
 * history stays navigable.
 */
function applyEdit(
  design: QrDesign,
  action: Exclude<QrAction, { type: 'reset' | 'undo' | 'redo' }>,
): { readonly design: QrDesign; readonly tag: string | null } {
  switch (action.type) {
    case 'setContent':
      return { design: { ...design, content: action.content }, tag: 'content' };
    case 'setErrorCorrectionLevel':
      return { design: { ...design, errorCorrectionLevel: action.level }, tag: null };
    case 'setSize':
      return { design: { ...design, size: action.size }, tag: null };
    case 'setModuleStyle':
      return { design: { ...design, moduleStyle: action.style }, tag: null };
    case 'setModuleColor':
      return { design: { ...design, moduleColor: action.color }, tag: 'moduleColor' };
    case 'setBackgroundColor':
      return { design: { ...design, backgroundColor: action.color }, tag: 'backgroundColor' };
    case 'setOuterEyeStyle':
      return { design: { ...design, outerEyeStyle: action.style }, tag: null };
    case 'setInnerEyeStyle':
      return { design: { ...design, innerEyeStyle: action.style }, tag: null };
    case 'setEyeColor':
      return { design: { ...design, eyeColor: action.color }, tag: 'eyeColor' };
    case 'setOuterBorderRadius':
      return { design: { ...design, outerBorderRadius: action.radius }, tag: 'outerRadius' };
    case 'setInnerBorderRadius':
      return { design: { ...design, innerBorderRadius: action.radius }, tag: 'innerRadius' };
    case 'setStyleEyesAsModules':
      return { design: { ...design, styleEyesAsModules: action.enabled }, tag: null };
    case 'setCenterImage':
      return { design: { ...design, centerImage: action.file }, tag: null };
    case 'setCenterImageSize':
      return { design: { ...design, centerImageSize: action.size }, tag: 'centerImageSize' };
    case 'setCustomModuleImage':
      return { design: { ...design, customModuleImage: action.file }, tag: null };
  }
}

export function qrReducer(state: QrState, action: QrAction): QrState {
  // History navigation is handled first; everything else is a design edit,
  // narrowed for `applyEdit`'s exhaustive switch.
  if (action.type === 'undo') return undo(state);
  if (action.type === 'redo') return redo(state);
  if (action.type === 'reset') return record(state, DEFAULT_DESIGN, null, HISTORY_LIMIT);

  const { design, tag } = applyEdit(state.present, action);
  return record(state, design, tag, HISTORY_LIMIT);
}
